import {
  AUTOMATION_STEPS,
  STEP_NODE_TYPES,
  edgeLabels,
  getStepConfig,
  defaultTrigger,
} from '../steps';

// ToastProvider pulls in fonts that blow up jest
const toastVariant = {
  SUCCESS: 'success',
  ALERT: 'alert',
  FAILURE: 'failure',
};

import { BranchStepTypes } from 'pages/AutomationEngine/types/automation';

import {
  deleteStrategies,
  unreachableStepsResolutions,
} from '../Engine/dialogs/constants';

import {
  checkTriggersInUseByVariable,
  removeInUseByTriggerType,
  checkChangeInTrigger,
} from 'pages/AutomationEngine/dialogs/GoalAndTriggerWizard/variablesInTriggerHelpers';

import {
  checkVariableUsedInActions,
  removeDeletedVariableFromActions,
} from 'pages/AutomationEngine/dialogs/ActionWizard/variablesInActionsHelpers';

import {
  getChildrenSteps,
  hasChildren,
  getImmediateChildrenIds,
  createMergeSplitStepData,
  createMergeStepData,
  isGoalOrCondition,
  getMergeAndSplitSteps,
  countTheLastLeavesFromBranch,
  findTheLastLeavesFromBranch,
  isBranchStep,
  isBranchSplit,
  createBranchStepData,
  createBranchSplitStepData,
  getStepsInMerge,
  getStepsInMergeMovingBranch,
  createLoopNextStepData,
  isLoopStep,
  isLoopStepNext,
  getLoopNextStep,
  getStepIdsInLoop,
  makeItemVariableName,
  getAllVariableNames,
  getUniqueItemVariableName,
  createItemVariableStepData,
  insertItemVariable,
  safeCurrent,
  getDescendantStepIds,
  getAllStepIdsInMergedBranches,
  getAllGoToStepDependenciesForSubtree,
} from './ts-utilities';
import { ensureNoGoToStepDependency } from './ts-utilities';

const ensureNoExtraBranches = (state, id) => {
  // When moving or deleting a step, we need to ensure
  // it doesn't leave any extra branches lying around.
  const hasSibling = state.steps.some(({ parentKey }) => id === parentKey);

  if (!hasSibling) {
    delete state.branches[id || STEP_NODE_TYPES.INITIALIZE_VARIABLE];
  }
  return state;
};

function findLastIndex(tree, parentKey, label = '') {
  const children = tree.filter(
    (el) => el.fromLabel === label && el.parentKey === parentKey
  );

  if (!children.length) {
    return 0;
  }

  const getLastIndex = (arr) => {
    const childrenIndexes = arr.map((el) =>
      tree.findIndex((curr) => curr.id === el.id)
    );
    return childrenIndexes[childrenIndexes.length - 1];
  };

  if (label === 'yes') {
    const filteredChildren = children.filter((el) => el.fromLabel === label);
    if (filteredChildren.length) {
      return getLastIndex(filteredChildren);
    }
    return 0;
  }

  if (label === 'no') {
    const filteredChildrenNo = children.filter((el) => el.fromLabel === label);
    if (filteredChildrenNo.length) {
      return getLastIndex(filteredChildrenNo);
    }

    const filteredChildrenYes = children.filter((el) => el.fromLabel === 'yes');
    if (filteredChildrenYes.length) {
      return getLastIndex(filteredChildrenYes);
    }
    return 0;
  }
}

const gredilyGetStepsAfterMerge = (tree, parentKey, stopAtId = undefined) => {
  if (!tree) {
    return [];
  }

  const root = tree.find((step) => step.id === parentKey);
  if (!root) {
    return [];
  }

  const leaves = [];
  const queue = [root];
  const initiators = [];

  while (queue.length > 0) {
    const current = queue.shift();

    // Everything from here on belongs to whatever encloses the merge rather
    // than following it - the walk stops short of it and its children.
    if (current.id === stopAtId) {
      continue;
    }

    if (current.isBranchGroupInitiator) {
      initiators.push(current.id);
    }

    // don't go past merge_branches that are not the parent
    leaves.push(current.id);
    const children = tree.filter((step) => step.parentKey === current.id);
    queue.push(...children);
  }

  return leaves;
};

export const moveOrInsertStep = (
  state,
  {
    step,
    id,
    parentKey,
    leaf,
    label,
    endOffBranch,
    loopDepth = 0,
    loopVariables = [],
  }
) => {
  // loopDepth and loopVariables are used to match the steps new location

  const stepConfig = getStepConfig(step.type);
  if (step.id && !stepConfig.movable) {
    // Currently we only know how to move action, delay, goal and condition types steps
    return state;
  }

  // if leaf is true adding from an new branch so we don't want to add another
  if (
    (leaf || endOffBranch) &&
    (parentKey || STEP_NODE_TYPES.INITIALIZE_VARIABLE) in state.branches
  ) {
    // When dropping on an open branch, ensure it doesn't stick around
    delete state.branches[parentKey || STEP_NODE_TYPES.INITIALIZE_VARIABLE];
  }
  const lastIndex = label && findLastIndex(state.steps, parentKey, label.type);
  const newEl = step.id
    ? {
        // Moving an existing step
        ...step,
        parentKey,
        fromLabel: label?.type || '',
        loopDepth,
        loopVariables,
      }
    : {
        // Adding a new step
        ...step,
        id: `new.${step.type}.${Date.now()}`,
        parentKey,
        fromLabel: label?.type || '',
        details: step.details || {},
        loopDepth,
        loopVariables,
      };
  state.dirty = true;

  state.steps = state.steps.flatMap((el) => {
    if (step.id && el.parentKey === step.id && !isBranchSplit(el)) {
      // Attach child of existing step to that step's parent unless it's a branch split as that goes with branch
      // This is the simplest move strategy, which we use for action and delay types.
      // Could apply to multiple opterations below, particularly the leaf case and fallthrough.
      el = {
        ...el,
        parentKey: step.parentKey,
        fromLabel: step.fromLabel,
      };
    }
    if (step.id && el.id === step.id) {
      // When moving a step, remove it from its previous position.
      return [];
    }
    if (!lastIndex && parentKey === el.id && (leaf || endOffBranch)) {
      // For leaves, place the element after the source node.
      return [el, newEl];
    }
    if (el.id === id) {
      // Add element before the target node, and update the target node's parent.
      // Placing the new element first helps keep the graph layout stable as nodes are added.
      return [
        newEl,
        {
          ...el,
          parentKey: newEl.id,
          fromLabel:
            newEl.type === STEP_NODE_TYPES.CONDITION || newEl.goalType
              ? edgeLabels.yes.type
              : '',
        },
      ];
    }
    return el;
  });

  if (lastIndex && (leaf || endOffBranch)) {
    //as we can create multiple branches from a condition/goal yes(no) branch we want to insert new node to the top of sibling nodes
    const firstChildIndex = state.steps.findIndex(
      (el) => el.fromLabel === label.type && el.parentKey === parentKey
    );
    state.steps = state.steps.reduce(
      (collect, el, index) =>
        firstChildIndex === index ? [...collect, newEl, el] : [...collect, el],
      []
    );
  }
  if (!parentKey && !id) {
    // Hey, we have a new root step! This is a direct child of variables.
    if (state.branches[STEP_NODE_TYPES.INITIALIZE_VARIABLE]) {
      // ensure there isn't a hanging trigger branch
      delete state.branches[STEP_NODE_TYPES.INITIALIZE_VARIABLE];
    }
    state.steps = [newEl, ...state.steps];
  }

  if (step.id) {
    // Ensure no bonus branches where step was dragged from
    ensureNoExtraBranches(state, step.parentKey);
  }

  if (['condition', 'goal'].includes(step.type)) {
    state.focusStep = newEl.id;
    state.focusStepToken = (state.focusStepToken || 0) + 1;
    state.focusStepBySearch &&= 'skip_search_focus';
  }

  return state;
};
export const moveBranchStep = (
  state,
  { step, id, parentKey, leaf, label, endOffBranch }
) => {
  // if leaf is true adding from an new branch so we don't want to add another
  if (
    (leaf || endOffBranch) &&
    (parentKey || STEP_NODE_TYPES.INITIALIZE_VARIABLE) in state.branches
  ) {
    // When dropping on an open branch, ensure it doesn't stick around
    delete state.branches[parentKey || STEP_NODE_TYPES.INITIALIZE_VARIABLE];
  }
  const lastIndex = label && findLastIndex(state.steps, parentKey, label.type);
  const newEl = {
    // Moving an existing step
    ...step,
    parentKey,
    fromLabel: label?.type || '',
  };

  const { splitStep } = getMergeAndSplitSteps(state.steps, newEl);

  state.dirty = true;
  state.steps = state.steps.flatMap((el, index) => {
    if (step.id && el.parentKey === step.id && !isBranchSplit(el)) {
      // Attach child of existing step to that step's parent unless it's a branch split as that goes with branch
      // This is the simplest move strategy, which we use for action and delay types.
      // Could apply to multiple opterations below, particularly the leaf case and fallthrough.
      el = {
        ...el,
        parentKey: step.parentKey,
        fromLabel: step.fromLabel,
      };
    }
    if (step.id && el.id === step.id) {
      // When moving a step, remove it from its previous position.
      return [];
    }
    if (!lastIndex && parentKey === el.id && (leaf || endOffBranch)) {
      // For leaves, place the element after the source node.
      return [el, newEl];
    }
    if (el.id === id) {
      // Add element before the target node, and update the target node's parent.
      // Placing the new element first helps keep the graph layout stable as nodes are added.
      return [
        newEl,
        {
          ...el,
          parentKey: splitStep.id,
          fromLabel:
            splitStep.type === STEP_NODE_TYPES.CONDITION || splitStep.goalType
              ? edgeLabels.yes.type
              : '',
        },
      ];
    }
    return el;
  });

  if (lastIndex && (leaf || endOffBranch)) {
    //as we can create multiple branches from a condition/goal yes(no) branch we want to insert new node to the top of sibling nodes
    const firstChildIndex = state.steps.findIndex(
      (el) => el.fromLabel === label.type && el.parentKey === parentKey
    );
    state.steps = state.steps.reduce(
      (collect, el, index) =>
        firstChildIndex === index ? [...collect, newEl, el] : [...collect, el],
      []
    );
  }

  if (!parentKey && !id) {
    // Hey, we have a new root step! This is a direct child of variables.
    if (state.branches[STEP_NODE_TYPES.INITIALIZE_VARIABLE]) {
      // ensure there isn't a hanging trigger branch
      delete state.branches[STEP_NODE_TYPES.INITIALIZE_VARIABLE];
    }
    state.steps = [newEl, ...state.steps];
  }

  if (step.id) {
    // Ensure no bonus branches where step was dragged from
    ensureNoExtraBranches(state, step.parentKey);
  }

  return state;
};

export const insertSplitStepAfterStep = (
  state,
  { id, isBranchGroupInitiator, goalType }
) => {
  let parentKeyMap = {};

  const stepsWithMergeSplits = state.steps.reduce((previousSteps, step) => {
    previousSteps.push(step);
    if (isGoalOrCondition(step) && step.id === id) {
      const mergeStep = createMergeSplitStepData(
        step.id,
        isBranchGroupInitiator,
        goalType,
        null,
        step.loopDepth,
        step.loopVariables
      );
      parentKeyMap = { ...parentKeyMap, [step.id]: mergeStep.id };
      previousSteps.push(mergeStep);
    }

    return previousSteps;
  }, []);

  if (Object.keys(parentKeyMap).length) {
    state.steps = stepsWithMergeSplits.map((step) => {
      const parentKey = parentKeyMap[step.parentKey];
      return parentKey && step.id !== parentKey ? { ...step, parentKey } : step;
    });
  }

  return state;
};

export const figureOutTheNewStep = (state) => {
  const prevIds = state.prevSteps.map(({ id }) => id);
  const newStep = state.steps.find(({ id }) => !prevIds.includes(id));
  return newStep;
};

// compares state.prevSteps & state.steps to figure out the new condition / goal step
export const insertSplitStep = (state) => {
  const newStep = figureOutTheNewStep(state);
  if (!newStep) {
    return state;
  }
  return insertSplitStepAfterStep(state, newStep);
};

export const insertBranchSplitStepAfterStep = (state, { id }) => {
  let parentKeyMap = {};

  const stepsWithMergeSplits = state.steps.reduce((previousSteps, step) => {
    previousSteps.push(step);
    if (isBranchStep(step) && step.id === id) {
      const mergeStep = createBranchSplitStepData(
        step.id,
        step.isBranchGroupInitiator,
        step.goalType,
        null,
        step.loopDepth,
        step.loopVariables
      );
      parentKeyMap = { ...parentKeyMap, [step.id]: mergeStep.id };
      previousSteps.push(mergeStep);
    }

    return previousSteps;
  }, []);

  if (Object.keys(parentKeyMap).length) {
    state.steps = stepsWithMergeSplits.map((step) => {
      const parentKey = parentKeyMap[step.parentKey];
      return parentKey && step.id !== parentKey ? { ...step, parentKey } : step;
    });
  }

  return state;
};

// compares state.prevSteps & state.steps to figure out the new branch split
export const insertBranchSplitStep = (state) => {
  const newStep = figureOutTheNewStep(state);

  if (!newStep) {
    return state;
  }

  const nextState = insertBranchSplitStepAfterStep(state, newStep);

  const { splitStep } = getMergeAndSplitSteps(nextState.steps, newStep);

  if (hasChildren(nextState.steps, splitStep)) {
    // remove this to always add a branch
    nextState.branches[splitStep.id] = true;
  }

  return nextState;
};

export const insertMergeStep = (state, { id, loopDepth, loopVariables }) => {
  let loopNextStep = undefined;
  let mergeStep = undefined;

  let nextSteps = state.steps.flatMap((step) => {
    if ((isGoalOrCondition(step) || isBranchStep(step)) && step.id === id) {
      mergeStep = createMergeStepData(
        step.id,
        '',
        step.loopDepth,
        step.loopVariables
      );
      return [step, mergeStep];
    }
    return step;
  });

  if (loopDepth) {
    // in a loop we need to connect the loop next step
    loopNextStep = nextSteps.find(
      (s) =>
        isLoopStepNext(s) && s.loopParent === loopVariables?.[0]?.loopStepId
    );

    // Only the group that closes the loop body owns the Next/Finish. A nested
    // goal or condition merges inside an enclosing group, so its merge sits in
    // the middle of the body - taking the Next/Finish there would pull it (and
    // the outer merge with it) out of the loop.
    const ownsLoopEnd =
      loopNextStep &&
      [mergeStep.id, id, ...getDescendantStepIds(nextSteps, id)].includes(
        loopNextStep.parentKey
      );

    if (ownsLoopEnd) {
      nextSteps = nextSteps.flatMap((step) => {
        if (step.id === loopNextStep.id) {
          return { ...step, parentKey: mergeStep.id, fromLabel: '' };
        }
        return step;
      });
    }
  }

  state.steps = nextSteps;
  return state;
};

export const removeMergeStep = (state, { joinStep }) => {
  if (!hasChildren(state.steps, joinStep)) {
    // const nextSteps = state.steps.reduce((previousSteps, step) => {
    //   if (step.id !== joinStep.id) {
    //     previousSteps.push(step);
    //   }
    //   return previousSteps;
    // }, []);
    const nextSteps = state.steps.flatMap((step) => {
      if (step.id === joinStep.id) {
        return [];
      }
      return step;
    });

    state.steps = nextSteps;
  }
  return state;
};

export const removeSplitStep = (state, { splitStep }) => {
  state.steps = state.steps.flatMap((step) => {
    if (step.id === splitStep.id) {
      return [];
    }

    if (step.parentKey === splitStep.id) {
      return { ...step, parentKey: splitStep.parentKey };
    }
    return step;
  }, []);
  return state;
};

export const setMergeParent = (state, { parentStepId, mergeParent }) => {
  if (!parentStepId) {
    return state;
  }

  const childIds = getChildrenSteps(parentStepId, [parentStepId], state.steps);

  const nextSteps = state.steps.map((step) => {
    if (childIds.includes(step.id)) {
      return { ...step, mergeParent };
    }
    return step;
  }, []);

  state.steps = nextSteps;
  return state;
};

export const editStep = (
  state,
  { step: { id, parentKey, ...others } },
  entityName = 'steps'
) => {
  const step = state[entityName].find((s) => s.id === id);
  if (step) {
    state.dirty = true;

    Object.assign(step, {
      ...others,
      hasError: false,
      stepError: null,
      parentKey,
      changeType: undefined,
    });

    // remove from error steps if need
    if (state.errorSteps.some((err) => err === id)) {
      state.deletedSteps = [...state.deletedSteps, id];

      state.errorSteps = state.errorSteps.filter((err) => err !== id);
    }
  }
  return state;
};

export const updateStep = (
  state,
  {
    step: { id, ...others },
    entityName = 'steps',
    queName = 'conditionDescriptionQue',
    dirty = true,
  }
) => {
  const step = state[entityName].find((s) => s.id === id);
  if (step) {
    Object.assign(step, { ...others });

    state[queName] = state[queName].filter((conditionId) => conditionId !== id);
    if (dirty) {
      state.dirty = true;
    }
  }
  return state;
};

export const clearDescriptionQue = (
  state,
  { queName = 'conditionDescriptionQue' }
) => {
  state[queName] = [];
  return state;
};

export const updateTrigger = (
  state,
  {
    trigger: { id, ...others },
    entityName = 'triggers',
    queName = 'triggerDescriptionQue',
    dirty = true,
  }
) => {
  const trigger = state[entityName].find((s) => s.id === id);
  if (trigger) {
    Object.assign(trigger, { ...others });

    state[queName] = state[queName].filter((conditionId) => conditionId !== id);
    if (dirty) {
      state.dirty = true;
    }
  }
  return state;
};

export const moveStepsAfterGotoMove = (
  state,
  { step: { id, ...others }, dropParent: { parentKey, label } },
  entityName = 'steps'
) => {
  const step = state[entityName].find((s) => s.id === id);
  if (step) {
    state.dirty = true;
    if (!label) {
      // not a branch
      Object.assign(step, { ...others, parentKey });
    }
  }
  return state;
};

export const setStepError = (
  state,
  { step: { id, parentKey, ...others } },
  entityName = 'steps'
) => {
  const step = state[entityName].find((s) => s.id === id);
  if (step) {
    Object.assign(step, { ...others, hasError: true, parentKey });
  }
  return state;
};

const setVariableStepErrors = (state, { step }) => {
  const { nextSteps, errorIds } = removeDeletedVariableFromActions(
    state.steps,
    step
  );

  if (errorIds.length) {
    for (const id of errorIds) {
      const step = state.steps.find((s) => s.id === id);
      if (step && !state.errorSteps.some((err) => err === id)) {
        state.errorSteps = [...state.errorSteps, id];
      }
    }

    state.toast = {
      variant: toastVariant.FAILURE,
      message: state.messageDictionary.variableUseInStep,
    };

    state.steps = nextSteps;
  }
  return state;
};

export const deleteStep = (
  state,
  { id, force = false },
  entityName = 'steps'
) => {
  const deleted = state[entityName].find((el) => el.id === id);
  if (!force) {
    const hasGoToStep = ensureNoGoToStepDependency(state.steps, deleted.id);
    const hasPendingExecutions =
      state.automation.numberActive > 0 || state.automation.numberPaused > 0;
    if (hasGoToStep || hasPendingExecutions) {
      state.interrupting = true;
      return state;
    }
  }

  if (!deleted) {
    return state;
  }

  state.dirty = true;
  state[entityName] = state[entityName].flatMap((el) => {
    if (el.id === id) {
      return []; // Remove
    }
    if (el.parentKey === id) {
      return {
        ...el,
        parentKey: deleted.parentKey,
        fromLabel: deleted.fromLabel,
      };
    }
    return el;
  });
  // did we delete from a condtion with an additonal branch ?
  if (
    state.branches[deleted.parentKey] &&
    state.branches[deleted.parentKey] === deleted.fromLabel
  ) {
    delete state.branches[deleted.parentKey];
  }
  // don't delete branch if the parent is the trigger block
  if (deleted.parentKey) {
    ensureNoExtraBranches(state, deleted.parentKey);
  }
  // clear branch from the step we are deleting
  if (state.branches[id]) {
    delete state.branches[id];
  }

  if (entityName === 'variables') {
    const inUse = checkVariableUsedInActions(state.steps, deleted);

    if (inUse) {
      state = setVariableStepErrors(state, { step: deleted });
    }
  }

  state.prevSteps = state.steps;
  state.interrupting = false;

  // remove from error steps if need
  if (state.errorSteps.some((err) => err === id)) {
    state.deletedSteps = [...state.deletedSteps, id];

    state.errorSteps = state.errorSteps.filter((err) => err !== id);
  }

  return state;
};

// good to have it for future
// eslint-disable-next-line no-unused-vars
function findLastInBranch(node, branch, condition) {
  const child = branch.find(
    (el) =>
      el.parentKey === node.id && (!el.fromLabel || el.fromLabel === condition)
  );
  if (child) {
    return findLastInBranch(child, branch, condition);
  }
  return node;
}

// Returns the "Go To" step at the end of the goal/condition's yes/no path, i.e. the
// step that moveBranch would attach the following steps to, or null if there isn't one
export const getPathEndGoToStep = (steps, step, strategy) => {
  const { splitStep } = getMergeAndSplitSteps(steps, step);
  if (!splitStep) {
    return null;
  }
  const firstElem = steps.find(
    (e) => e.parentKey === splitStep.id && e.fromLabel === strategy
  );
  const lastInBranch =
    firstElem && findLastInBranch(firstElem, steps, strategy);
  return lastInBranch?.type === AUTOMATION_STEPS.go_to_automation_step.type
    ? lastInBranch
    : null;
};

const isGoToStep = (step) =>
  step?.type === AUTOMATION_STEPS.go_to_automation_step.type;

// A "Go To" step ends its path, so nothing can follow it - but joining paths can
// leave steps hanging off one
export const getGoToStepsWithStepsAfter = (tree) =>
  tree.filter(
    (step) =>
      isGoToStep(step) && tree.some(({ parentKey }) => parentKey === step.id)
  );

// Removes the steps that hang off "Go To" steps, and everything after them
export const removeStepsAfterGoToSteps = (tree) => {
  const deletedIds = getGoToStepsWithStepsAfter(tree).flatMap((goTo) =>
    getDescendantStepIds(tree, goTo.id)
  );
  const deletedIdsSet = new Set(deletedIds);
  return {
    steps: tree.filter((s) => !deletedIdsSet.has(s.id)),
    deletedIds,
  };
};

// Handles the steps hanging off "Go To" steps the way the user chose
export const resolveStepsAfterGoToSteps = (
  tree,
  unreachableResolution,
  messageDictionary
) => {
  if (unreachableResolution === unreachableStepsResolutions.REMOVE) {
    return removeStepsAfterGoToSteps(tree).steps;
  }
  if (unreachableResolution === unreachableStepsResolutions.NEW_BRANCH) {
    return moveStepsAfterGoToStepsToNewBranch(tree, messageDictionary);
  }
  return tree;
};

// The store side of resolveStepsAfterGoToSteps: removed loops take their item
// variables with them, and when given a description the "Go To" steps targeting
// a removed step are flagged with an error
export const resolveUnreachableStepsAfterGoTo = (
  state,
  { unreachableResolution, deletedGoToDescription }
) => {
  if (unreachableResolution !== unreachableStepsResolutions.REMOVE) {
    state.steps = resolveStepsAfterGoToSteps(
      state.steps,
      unreachableResolution,
      state.messageDictionary
    );
    return state;
  }

  const { steps, deletedIds } = removeStepsAfterGoToSteps(state.steps);
  if (!deletedIds.length) {
    return state;
  }
  const removedLoops = state.steps.filter(
    (s) => isLoopStep(s) && deletedIds.includes(s.id)
  );
  state.steps = steps;
  deleteLoopItemVariables(state, removedLoops);
  if (deletedGoToDescription) {
    invalidateGoToStepsTargeting(state, deletedIds, deletedGoToDescription);
  }
  return state;
};

// Puts a split branch in front of each "Go To" step with steps after it: the
// "Go To" step goes to the upper branch and the steps after it to another one.
// When the "Go To" step already starts a split branch the steps join that split.
// The "Go To" steps are split one at a time, as one can hang off another.
export const moveStepsAfterGoToStepsToNewBranch = (
  tree,
  messageDictionary = {}
) => {
  for (let index = 0; ; index++) {
    const [goTo] = getGoToStepsWithStepsAfter(tree);

    if (!goTo) {
      return tree;
    }

    const parentStep = tree.find(({ id }) => id === goTo.parentKey);
    let stepsToInsert = [];
    let splitId = parentStep?.id;

    if (!isBranchSplit(parentStep) || parentStep.isBranchMerged) {
      // several "Go To" steps can be split in the same tick
      const newBranchId = `new.${BranchStepTypes.BRANCH}.${Date.now()}.${index}`;
      const branch = createBranchStepData(
        newBranchId,
        goTo.parentKey,
        goTo.fromLabel,
        false,
        messageDictionary.runBranchesInParallel,
        goTo.loopDepth,
        goTo.loopVariables
      );
      const branchSplit = createBranchSplitStepData(
        newBranchId,
        false,
        false,
        null,
        goTo.loopDepth,
        goTo.loopVariables
      );
      stepsToInsert = [branch, branchSplit];
      splitId = branchSplit.id;
    }

    tree = tree.flatMap((el) => {
      if (el.id !== goTo.id && el.parentKey !== goTo.id) {
        return el;
      }

      return [
        ...(el.id === goTo.id ? stepsToInsert : []),
        { ...el, parentKey: splitId, fromLabel: '', parentCondition: '' },
      ];
    });
  }
};

// Clears the target of every "Go To" step that targets one of the deleted steps and
// flags it with an error, so it can't be saved pointing at a step that no longer exists
export const invalidateGoToStepsTargeting = (
  state,
  deletedIds,
  deletedGoToDescription
) => {
  getAllGoToStepDependenciesForSubtree(state.steps, deletedIds).forEach(
    ({ id }) => {
      const step = state.steps.find((s) => s.id === id);
      // eslint-disable-next-line no-unused-vars
      const { stepKey, triggerKey, ...details } = step.details;
      Object.assign(step, {
        details,
        description: deletedGoToDescription,
        hasError: true,
      });
      if (!state.errorSteps.includes(id)) {
        state.errorSteps = [...state.errorSteps, id];
      }
      // a previously repaired step is in deletedSteps, which would stop it being
      // registered as an error again
      state.deletedSteps = state.deletedSteps.filter((d) => d !== id);
    }
  );
  return state;
};

// Removes the step and everything after it
export const removeStepAndDescendants = (steps, id) => {
  const deletedIds = [id, ...getDescendantStepIds(steps, id)];
  const deletedIdsSet = new Set(deletedIds);
  return {
    steps: steps.filter((s) => !deletedIdsSet.has(s.id)),
    deletedIds,
  };
};

export function deleteNode(tree, nodeToDelete) {
  const { splitStep, mergeStep } = getMergeAndSplitSteps(tree, nodeToDelete);

  const newTree = tree.flatMap((el) => {
    if (
      el.id === nodeToDelete.id ||
      el.id === splitStep?.id ||
      el.id === mergeStep?.id
    ) {
      return []; // Remove the nodeToDelete, ?splitStep, ?mergeStep
    }
    if (
      el.parentKey !== nodeToDelete.id &&
      el.parentKey !== splitStep?.id &&
      el.parentKey !== mergeStep?.id
    ) {
      return el;
    }
    return {
      ...el,
      parentKey: nodeToDelete.parentKey,
      fromLabel: nodeToDelete.fromLabel,
    };
  });
  return newTree;
}

const getIdsInLoop = (tree, loopStart, loopEnd) => {
  if (!tree || !loopStart || !loopEnd) {
    return [];
  }

  const ids = [];
  const nestedLoops = [];

  const queue = [loopStart];

  while (queue.length) {
    const current = queue.shift();

    if (current.id === loopEnd.id) {
      // Don't walk past the loop, but keep draining the queue: a merge step
      // can reach the Next/Finish before a merged goal's longer paths are done.
      ids.push(current.id);
      continue;
    }

    let children = tree.filter((step) => step.parentKey === current.id);

    if (current.id === loopStart.id) {
      ids.push(current.id);
    } else if (isLoopStep(current)) {
      nestedLoops.push(current);
      const loopNextStep = tree.find(
        (s) => isLoopStepNext(s) && s.loopParent == current.id
      );
      const { idsInLoop, loopsInLoop } = getIdsInLoop(
        tree,
        current,
        loopNextStep
      );

      ids.push(...idsInLoop);
      nestedLoops.push(...loopsInLoop);

      children = tree.filter((step) => step.parentKey === loopNextStep.id);
    } else {
      ids.push(current.id);
    }
    queue.push(...children);
  }

  return { idsInLoop: ids, loopsInLoop: nestedLoops };
};

export function deleteLoop(state, loopToDelete) {
  const { steps: tree } = state;
  const loopNextStep = tree.find(
    (s) => isLoopStepNext(s) && s.loopParent == loopToDelete.id
  );

  const { idsInLoop, loopsInLoop } = getIdsInLoop(
    tree,
    loopToDelete,
    loopNextStep
  );
  const newSteps = tree.flatMap((el) => {
    if (
      el.id === loopToDelete.id ||
      el.id === loopNextStep?.id ||
      idsInLoop.includes(el.id)
    ) {
      return []; // Remove the loopToDelete, ?splitStep, ?mergeStep
    }
    if (el.parentKey === loopNextStep.id) {
      return {
        ...el,
        parentKey: loopToDelete.parentKey,
        fromLabel: loopToDelete.fromLabel ?? '',
      };
    }
    return el;
  });
  state.steps = newSteps;
  state.dirty = true;

  // Forced: the loop deletion is already confirmed through LoopDeletionModal
  deleteLoopItemVariables(state, [loopToDelete, ...loopsInLoop]);

  return state;
}

// Deletes the item reference variables of deleted loops, as without it a live
// automation would keep the variables of a loop that no longer exists. Actions
// still using a deleted variable are flagged with errors by deleteStep.
export function deleteLoopItemVariables(state, loops) {
  loops.forEach((loop) => {
    const itemVariable = loop.details?.forEachItem?.itemVariable;

    if (itemVariable) {
      const varToDelete = state.variables.find((v) => {
        return v?.details?.variable?.name === itemVariable;
      });

      if (varToDelete) {
        state.deleteStepType = STEP_NODE_TYPES.INITIALIZE_VARIABLE;
        state.stepToDelete = {
          id: varToDelete.id,
          type: STEP_NODE_TYPES.INITIALIZE_VARIABLE,
        };
        deleteStep(state, { id: varToDelete.id, force: true }, 'variables');
      }
    }
  });

  return state;
}

export function deleteNodeAndInsertBranch(
  tree,
  nodeToDelete,
  messageDictionary
) {
  const { splitStep, mergeStep } = getMergeAndSplitSteps(tree, nodeToDelete);

  const newBranchId = `new.${BranchStepTypes.BRANCH}.${Date.now()}`;
  // A merged goal/condition becomes a branch that merges again, so the merge
  // step and the steps after it stay where they are
  const isMerged = !!mergeStep;

  // The branch takes the deleted step's place, so it hangs off the same path of
  // the parent - a goal/condition's yes/no path when nested in one
  const branch = {
    ...createBranchStepData(
      newBranchId,
      nodeToDelete.parentKey,
      nodeToDelete.fromLabel ?? '',
      isMerged,
      isMerged
        ? messageDictionary.runBranchesInParallelThenMerge
        : messageDictionary.runBranchesInParallel,
      nodeToDelete.loopDepth,
      nodeToDelete.loopVariables
    ),
    parentCondition: nodeToDelete.parentCondition,
    mergeParent: nodeToDelete.mergeParent,
  };

  const branchSplit = createBranchSplitStepData(
    newBranchId,
    isMerged,
    false,
    isMerged ? newBranchId : null,
    splitStep.loopDepth,
    splitStep.loopVariables
  );

  // the steps in the merged paths now merge into the branch
  const mergeIntoBranch = (el) =>
    el.mergeParent === nodeToDelete.id
      ? { ...el, mergeParent: newBranchId }
      : el;

  const isPathStart = (el) =>
    el.id !== splitStep?.id &&
    el.id !== mergeStep?.id &&
    (el.parentKey === nodeToDelete.id || el.parentKey === splitStep?.id);

  // A branch draws its paths in the order of the steps, so the yes path goes
  // first to stay the upper one, as it was on the goal/condition
  const paths = tree
    .filter(isPathStart)
    .sort(
      (a, b) =>
        Number(b.fromLabel === edgeLabels.yes.type) -
        Number(a.fromLabel === edgeLabels.yes.type)
    )
    .map((el) => ({
      ...mergeIntoBranch(el),
      parentKey: branchSplit.id,
      fromLabel: '',
    }));

  let branchInserted = false;

  const newTree = tree.flatMap((el) => {
    if (isMerged && el.id === mergeStep.id) {
      const merge = { ...el, parentKey: newBranchId, mergeParent: newBranchId };
      return paths.length ? merge : [branch, branchSplit, merge];
    }
    if (el.id === nodeToDelete.id || el.id === splitStep?.id) {
      return []; // Remove the nodeToDelete, ?splitStep
    }
    if (!isPathStart(el)) {
      return mergeIntoBranch(el);
    }
    if (branchInserted) {
      return []; // already inserted with the first path
    }

    branchInserted = true;
    return [branch, branchSplit, ...paths];
  });
  return newTree;
}

function deleteLastNode(tree, node) {
  const child = tree.find((el) => el.parentKey === node.id);
  if (child) {
    return deleteLastNode(tree, child);
  }

  return deleteNode(tree, node);
}

export function deleteBranchStrategy(tree, id) {
  let deleted = tree.find((el) => el.id === id);
  if (!deleted) {
    return tree;
  }

  do {
    tree = deleteLastNode(tree, deleted);
    deleted = tree.find((el) => el.id === id);
  } while (deleted);
  return tree;
}

export const branchMergePathStrategy = (tree, deleted) => {
  if (!deleted) {
    return tree;
  }

  const { splitStep, mergeStep } = getMergeAndSplitSteps(tree, deleted);

  const parentStep = splitStep || deleted; // could be be splitStep or goal

  const mergeChildren = getImmediateChildrenIds(tree, mergeStep);

  const yesLeavesCount = countTheLastLeavesFromBranch(
    tree,
    parentStep.id,
    edgeLabels.yes.type
  );

  const noLeavesCount = countTheLastLeavesFromBranch(
    tree,
    parentStep.id,
    edgeLabels.no.type
  );

  if (yesLeavesCount > 1 || noLeavesCount > 1) {
    return tree;
  }

  const lastSibling = findTheLastLeavesFromBranch(
    tree,
    parentStep.id,
    noLeavesCount !== 0 ? edgeLabels.no.type : edgeLabels.yes.type
  );

  const stepIdToAttachMergeChildrenTo = lastSibling.length
    ? lastSibling[0].id
    : deleted.parentKey;

  tree = tree.flatMap((el) => {
    if (
      el.id === deleted.id ||
      el.id === splitStep?.id ||
      el.id === mergeStep?.id
    ) {
      return []; // Remove
    }
    if (el.parentKey === parentStep.id && el.fromLabel) {
      // if fromLabel === 'no' - we want to merge with 'yes' branch
      const yesConditionSibling = tree.find(
        (n) =>
          n.parentKey === parentStep.id && n.fromLabel === edgeLabels.yes.type
      );

      if (el.fromLabel === edgeLabels.yes.type || !yesConditionSibling) {
        return {
          ...el,
          parentKey: deleted.parentKey,
          fromLabel: deleted.fromLabel,
        };
      }

      if (yesConditionSibling) {
        const leafInYesBranch = findLastInBranch(
          yesConditionSibling,
          tree,
          'yes'
        );

        return {
          ...el,
          parentKey: leafInYesBranch.id,
          fromLabel: ['condition', 'goal', BranchStepTypes.SPLIT].includes(
            leafInYesBranch.type
          )
            ? leafInYesBranch.fromLabel
            : '',
        };
      }
    }

    if (mergeChildren.includes(el.id) && stepIdToAttachMergeChildrenTo) {
      return {
        ...el,
        parentKey: stepIdToAttachMergeChildrenTo,
        fromLabel: lastSibling.length ? el.fromLabel : deleted.fromLabel,
      };
    }

    return el;
  });

  return tree;
};

export const mergePathStrategy = (tree, id) => {
  const deleted = tree.find((el) => el.id === id);

  if (!deleted) {
    return tree;
  }

  if (deleted.isBranchGroupInitiator) {
    return branchMergePathStrategy(tree, deleted);
  }

  const { splitStep } = getMergeAndSplitSteps(tree, deleted);

  const parentStep = splitStep || deleted; // could be be splitStep or goal

  tree = tree.flatMap((el) => {
    if (el.id === deleted.id || el.id === splitStep?.id) {
      return []; // Remove
    }
    if (el.parentKey === parentStep.id && el.fromLabel) {
      // if fromLabel === 'no' - we want to marge with 'yes' branch
      const yesConditionSibling = tree.find(
        (n) =>
          n.parentKey === parentStep.id && n.fromLabel === edgeLabels.yes.type
      );
      if (el.fromLabel === edgeLabels.yes.type || !yesConditionSibling) {
        return {
          ...el,
          parentKey: deleted.parentKey,
          fromLabel: deleted.fromLabel,
        };
      }
      if (yesConditionSibling) {
        const leafInYesBranch = findLastInBranch(
          yesConditionSibling,
          tree,
          'yes'
        );

        return {
          ...el,
          parentKey: leafInYesBranch.id,
          fromLabel: ['condition', 'goal'].includes(leafInYesBranch.type)
            ? leafInYesBranch.fromLabel
            : '',
        };
      }
    }
    return el;
  });

  return tree;
};

export const leaveBothStrategy = (tree, id) => {
  const deleted = tree.find((el) => el.id === id);
  if (!deleted) {
    return tree;
  }

  tree = deleteNode(tree, deleted);

  return tree;
};

export const leaveBothStrategyAndInsertBranch = (
  tree,
  id,
  messageDictionary
) => {
  const deleted = tree.find((el) => el.id === id);
  if (!deleted) {
    return tree;
  }

  tree = deleteNodeAndInsertBranch(tree, deleted, messageDictionary);

  return tree;
};

export const branchKeepOnlyStrategy = (tree, deleted, fromLabelToKeep) => {
  if (!deleted) {
    return tree;
  }

  const { splitStep, mergeStep } = getMergeAndSplitSteps(tree, deleted);

  const parentStep = splitStep || deleted; // could be be splitStep or goal

  const mergeChildren = getImmediateChildrenIds(tree, mergeStep);

  const leavesCount = countTheLastLeavesFromBranch(
    tree,
    parentStep.id,
    fromLabelToKeep
  );

  if (leavesCount > 1) {
    return tree;
  }

  const lastSibling = findTheLastLeavesFromBranch(
    tree,
    parentStep.id,
    fromLabelToKeep
  );

  const stepIdToAttachMergeChildrenTo = lastSibling.length
    ? lastSibling[0].id
    : deleted.parentKey;

  const branchesToRemoved = tree.filter(
    (el) => el.parentKey === parentStep.id && el.fromLabel !== fromLabelToKeep
  );

  branchesToRemoved.forEach((branch) => {
    tree = deleteBranchStrategy(tree, branch.id);
  });

  tree = deleteNode(tree, deleted);

  if (mergeChildren?.length) {
    tree = tree.flatMap((el) => {
      if (mergeChildren.includes(el.id) && stepIdToAttachMergeChildrenTo) {
        return {
          ...el,
          parentKey: stepIdToAttachMergeChildrenTo,
          fromLabel: lastSibling.length ? '' : el.fromLabel,
        };
      }
      return el;
    });
  }
  return tree;
};

export const keepOnlyStrategy = (tree, id, fromLabelToKeep) => {
  const deleted = tree.find((el) => el.id === id);

  if (!deleted) {
    return tree;
  }

  if (deleted.isBranchGroupInitiator) {
    return branchKeepOnlyStrategy(tree, deleted, fromLabelToKeep);
  }

  const { splitStep } = getMergeAndSplitSteps(tree, deleted);

  const parentStep = splitStep || deleted; // could be be splitStep or goal

  const branchesToRemoved = tree.filter(
    (el) => el.parentKey === parentStep.id && el.fromLabel !== fromLabelToKeep
  );

  // eslint-disable-next-line no-plusplus
  branchesToRemoved.forEach((branch) => {
    tree = deleteBranchStrategy(tree, branch.id);
  });

  tree = deleteNode(tree, deleted);

  return tree;
};

export const deleteAllAfterConditionStrategy = (tree, id) => {
  const deleted = tree.find((el) => el.id === id);
  if (!deleted) {
    return tree;
  }
  const { splitStep, mergeStep } = getMergeAndSplitSteps(tree, deleted);

  if (!splitStep || !mergeStep) {
    return tree;
  }

  const stepsInMerge = getStepsInMerge(tree, id, mergeStep?.id);

  // A loop's Next/Finish closes the loop rather than following the condition,
  // so it outlives the delete and takes the condition's place. Everything
  // after it is outside the loop, and outside the scope of this strategy.
  const enclosingLoopId = deleted.loopVariables?.[0]?.loopStepId;
  const loopNextStep = enclosingLoopId
    ? tree.find(
        (step) => isLoopStepNext(step) && step.loopParent === enclosingLoopId
      )
    : undefined;

  const stepsAfterMerge = gredilyGetStepsAfterMerge(
    tree,
    mergeStep.id,
    loopNextStep?.id
  );

  const closesLoop =
    !!loopNextStep && stepsAfterMerge.includes(loopNextStep.parentKey);

  const newtree = tree.flatMap((el) => {
    if (
      el.id === deleted.id ||
      el.id === splitStep?.id ||
      el.id === mergeStep?.id ||
      stepsInMerge.includes(el.id) ||
      stepsAfterMerge.includes(el.id)
    ) {
      return []; // Remove
    }
    if (closesLoop && el.id === loopNextStep.id) {
      return {
        ...el,
        parentKey: deleted.parentKey,
        fromLabel: deleted.fromLabel ?? '',
      };
    }
    return el;
  });

  return newtree;
};

export const keepStepsAfterMergeBranchesStrategy = (tree, id) => {
  const deleted = tree.find((el) => el.id === id);

  if (!deleted) {
    return tree;
  }

  const { splitStep, mergeStep } = getMergeAndSplitSteps(tree, deleted);

  if (!splitStep || !mergeStep) {
    return tree;
  }

  const stepsInMerge = getStepsInMerge(tree, id, mergeStep?.id);

  const mergeChildren = getImmediateChildrenIds(tree, mergeStep);

  const newtree = tree.flatMap((el) => {
    if (
      el.id === deleted.id ||
      el.id === splitStep?.id ||
      el.id === mergeStep?.id ||
      stepsInMerge.includes(el.id)
    ) {
      return []; // Remove
    }
    if (mergeChildren.includes(el.id)) {
      return {
        ...el,
        parentKey: deleted.parentKey,
        fromLabel: deleted.fromLabel,
        parentCondition: deleted.parentCondition,
      };
    }
    return el;
  });

  return newtree;
};

const getDeletedStepIds = (tree, newTree) => {
  const remainingIds = new Set(newTree.map((s) => s.id));
  return tree.reduce((acc, s) => {
    if (!remainingIds.has(s.id)) {
      acc.push(s.id);
    }
    return acc;
  }, []);
};

// The tree a goal/condition delete strategy leaves behind. The move strategies
// that rearrange the paths share their values with these.
export const applyBranchDeletionStrategy = (
  tree,
  id,
  strategy,
  messageDictionary = {}
) => {
  if (strategy === deleteStrategies.DELETE_ALL_STEPS_AFTER_CONDITION) {
    return deleteAllAfterConditionStrategy(tree, id);
  }
  if (strategy === deleteStrategies.KEEP_STEPS_AFTER_MERGE_BRANCHES) {
    return keepStepsAfterMergeBranchesStrategy(tree, id);
  }
  if (strategy === deleteStrategies.MERGE_PATH_STRATEGY) {
    return mergePathStrategy(tree, id);
  }
  if (strategy === deleteStrategies.LEAVE_BOTH_PATHS_SPLIT_STRATEGY) {
    return leaveBothStrategyAndInsertBranch(tree, id, messageDictionary);
  }
  if (strategy === deleteStrategies.KEEP_ONLY_YES_PATH_STRATEGY) {
    return keepOnlyStrategy(tree, id, 'yes');
  }
  if (strategy === deleteStrategies.KEEP_ONLY_NO_PATH_STRATEGY) {
    return keepOnlyStrategy(tree, id, 'no');
  }
  if (!strategy) {
    return deleteBranchStrategy(tree, id);
  }
  return tree;
};

// Whether the strategy would leave steps after a "Go To" step, where they can
// never be reached, so the user has to choose how to handle them
export const doesStrategyLeaveStepsAfterGoTo = (tree, id, strategy) =>
  !!strategy &&
  getGoToStepsWithStepsAfter(applyBranchDeletionStrategy(tree, id, strategy))
    .length > 0;

export const getBranchDeletionDeletedStepIds = (
  tree,
  id,
  strategy,
  messageDictionary = {},
  unreachableResolution
) => {
  const newTree = resolveStepsAfterGoToSteps(
    applyBranchDeletionStrategy(tree, id, strategy, messageDictionary),
    unreachableResolution,
    messageDictionary
  );

  return getDeletedStepIds(tree, newTree);
};

export const daisyChainVariableToActionErrors = (state, { step }) => {
  const inUse = checkVariableUsedInActions(state.steps, step);

  if (inUse) {
    state = setVariableStepErrors(state, { step });
  }

  return state;
};

export const setVariableErrors = (state, { step }) => {
  const { variables, errorIds } = removeInUseByTriggerType(
    state.variables,
    step
  );

  if (errorIds.length) {
    for (const id of errorIds) {
      const step = state.variables.find((variable) => variable.id === id);
      if (step) {
        state = daisyChainVariableToActionErrors(state, { step });
      }
    }

    for (const id of errorIds) {
      const step = state.variables.find((variable) => variable.id === id);
      if (step && !state.errorSteps.some((err) => err === id)) {
        state.errorSteps = [...state.errorSteps, id];
      }
    }

    state.toast = {
      variant: toastVariant.FAILURE,
      message: state.messageDictionary.activityUsedAsVariableSource,
    };

    state.variables = variables;
  }
  return state;
};

export const deleteTrigger = (state, { id, force = false }) => {
  if (!force && ensureNoGoToStepDependency(state.steps, id)) {
    state.interrupting = true;
    return state;
  }
  const data = state.triggers.find((t) => t.id === id);
  // add error for any variable using this trigger

  const { inUse, count } = checkTriggersInUseByVariable(
    state.variables,
    state.triggers,
    data
  );

  if (inUse && count <= 1) {
    state = setVariableErrors(state, { step: data });
  }

  const firstTrigger = state.triggers[0];
  if (state.triggers.length === 1) {
    if (firstTrigger.type !== 'manual') {
      state.triggers[0] = {
        ...defaultTrigger,
        id: `${defaultTrigger.id}.${Date.now()}`,
        order: state.triggers.length,
      };
      state.dirty = true;
    }
  } else {
    if (!id.startsWith('new')) {
      state.dirty = true;
    }
    state.triggers = state.triggers.filter((trigger) => trigger.id !== id);
  }

  // triggers are part of error steps, remove  if need
  if (state.errorSteps.some((err) => err === id)) {
    state.deletedSteps = [...state.deletedSteps, id];

    state.errorSteps = state.errorSteps.filter((err) => err !== id);
  }

  state.interrupting = false;
  return state;
};

export const checkTriggerUsedByVariable = (state, { step }) => {
  // need to see if it was in use
  const { inUse, count } = checkTriggersInUseByVariable(
    state.variables,
    state.triggers,
    state.editStepInfo.step
  );

  // add error for any variable using this trigger
  if (
    inUse &&
    count <= 1 &&
    checkChangeInTrigger(state.editStepInfo.step, step)
  ) {
    state = setVariableErrors(state, { step: state.editStepInfo.step });
  }

  return state;
};

export const moveBranch = (
  state,
  { step, id, parentKey, leaf, endOffBranch, label, strategy }
) => {
  const stepConfig = getStepConfig(step.type);
  if (step.id && !stepConfig.movable) {
    // Currently we only know how to move action, delay, goal and condition types steps
    return state;
  }

  const { splitStep } = getMergeAndSplitSteps(state.steps, step);

  if (!splitStep) {
    return state;
  }

  const lastIndex = label && findLastIndex(state.steps, parentKey, label.type);

  const newEl = {
    // Moving an existing step
    ...step,
    parentKey,
    fromLabel: label?.type || '',
  };

  state.dirty = true;
  state.steps = state.steps.flatMap((el, index) => {
    if (step.id && el.parentKey === step.id) {
      // Attach child of existing step to that step's parent.
      // This is the simplest move strategy, which we use for action and delay types.
      // Could apply to multiple opterations below, particularly the leaf case and fallthrough.
      el = {
        ...el,
      };
    }

    if (step.id && el.id === step.id) {
      // When moving a step, remove it from its previous position.
      return [];
    }

    if (lastIndex && lastIndex === index && (leaf || endOffBranch)) {
      return [el, newEl];
    }

    if (!lastIndex && el.id === parentKey && (leaf || endOffBranch)) {
      // For leaves, place the element after the source node.
      return [el, newEl];
    }

    const firstElem = state.steps.find(
      (e) => e.parentKey === splitStep.id && e.fromLabel === strategy
    );

    if (el.id === id) {
      // Add element before the target node, and update the target node's parent.
      // Placing the new element first helps keep the graph layout stable as nodes are added.
      const lastInBranch =
        firstElem && findLastInBranch(firstElem, state.steps, strategy);

      let parentKey = undefined;

      if (isGoalOrCondition(step)) {
        // if we are moving a condition then we need to handle the merge step...
        parentKey = firstElem
          ? findLastInBranch(firstElem, state.steps, strategy).id
          : splitStep.id;
      } else {
        parentKey = firstElem
          ? findLastInBranch(firstElem, state.steps, strategy).id
          : newEl.id;
      }

      return [
        newEl,
        {
          ...el,
          parentKey,
          fromLabel: lastInBranch
            ? ['condition', 'goal', 'split_branches'].includes(
                lastInBranch.type
              )
              ? edgeLabels.yes.type
              : ''
            : strategy,
        },
      ];
    }
    return el;
  });

  if (!parentKey && !id) {
    // Hey, we have a new root step! This is a direct child of variables.
    if (state.branches[STEP_NODE_TYPES.INITIALIZE_VARIABLE]) {
      // ensure there isn't a hanging trigger branch
      delete state.branches[STEP_NODE_TYPES.INITIALIZE_VARIABLE];
    }
    state.steps = [newEl, ...state.steps];
  }

  if (step.id) {
    // Ensure no bonus branches where step was dragged from
    ensureNoExtraBranches(state, step.parentKey);
  }

  if (['condition', 'goal'].includes(step.type)) {
    state.focusStep = newEl.id;
    state.focusStepToken = (state.focusStepToken || 0) + 1;
    state.focusStepBySearch &&= 'skip_search_focus';
  }

  return state;
};

// Moves a goal/condition using the add-following-steps-to-yes/no strategy, and if the
// chosen path ends in a "Go To" step either removes the following steps (they'd be
// unreachable) or puts them in a new branch parallel to the moved goal/condition
export const moveBranchAndResolveUnreachableSteps = (
  state,
  { unreachableResolution, deletedGoToDescription, ...payload }
) => {
  const { step, id: followingId, strategy } = payload;
  const pathEndGoToStep =
    followingId && getPathEndGoToStep(state.steps, step, strategy);

  moveBranch(state, payload);

  if (!pathEndGoToStep || !unreachableResolution) {
    return state;
  }

  if (unreachableResolution === unreachableStepsResolutions.REMOVE) {
    const { steps, deletedIds } = removeStepAndDescendants(
      state.steps,
      followingId
    );
    const removedLoops = state.steps.filter(
      (s) => isLoopStep(s) && deletedIds.includes(s.id)
    );
    state.steps = steps;
    deleteLoopItemVariables(state, removedLoops);
    invalidateGoToStepsTargeting(state, deletedIds, deletedGoToDescription);
    return state;
  }

  const movedStep = state.steps.find((s) => s.id === step.id);
  const followingStep = state.steps.find((s) => s.id === followingId);
  const isBranchGroupInitiator = !!getAllStepIdsInMergedBranches(state.steps)[
    movedStep.parentKey
  ];

  state.steps = wrapStepWithBranch({
    steps: state.steps,
    stepToWrap: movedStep,
    parentKey: movedStep.parentKey,
    insertStepId: movedStep.id,
    isBranchGroupInitiator,
    replace: true,
    additionBranchHead: followingStep,
    messageDictionary: state.messageDictionary,
  }).newSteps;

  return state;
};

export const mergePaths = (state, { step, description }) => {
  const ogStep = state.steps.find(({ id }) => id === step?.id);

  const splitStep = state.steps.find(
    ({ parentKey, type }) =>
      parentKey === step?.id &&
      (type === BranchStepTypes.SPLIT || type === BranchStepTypes.BRANCH_SPLIT)
  );

  if (ogStep && splitStep) {
    Object.assign(ogStep, {
      isBranchGroupInitiator: true,
      description: description || ogStep.description,
    });
    Object.assign(splitStep, { isBranchMerged: true });
    state = insertMergeStep(state, step);
    state.dirty = true;
    state = setMergeParent(state, {
      parentStepId: step?.id,
      mergeParent: step?.id,
    });
  }

  return state;
};

// this is used to merged condition, goal and branch steps
// loopDepth and loopVariables are used to match the steps new location
// Walk a moving group depth first from its root so every branch inherits the
// depth of its own parent. Walking the ids in list order instead would let a
// loop on one path raise the depth of the steps on a sibling path.
const getLoopContextForMovingGroup = (
  stepsById,
  childrenByParent,
  movingGroup,
  rootId,
  rootDepth,
  rootVariables
) => {
  const updated = {};

  const walk = (id, depth, variables) => {
    const movingStep = stepsById[id];

    if (!movingStep) {
      return;
    }

    let nextDepth = depth;
    let nextVariables = variables;

    if (isLoopStep(movingStep)) {
      nextDepth++;
      nextVariables = [
        {
          loopStepId: movingStep.id,
          variableName: movingStep?.details?.forEachItem?.arrayVariable?.label,
        },
        ...variables,
      ];
    }

    if (isLoopStepNext(movingStep)) {
      nextDepth--;
      nextVariables = variables.slice(1);
    }

    if (movingGroup.has(id)) {
      updated[id] = {
        ...movingStep,
        loopDepth: nextDepth,
        loopVariables: [...nextVariables],
      };
    }

    (childrenByParent[id] ?? []).forEach((child) => {
      if (movingGroup.has(child.id)) {
        walk(child.id, nextDepth, nextVariables);
      }
    });
  };

  walk(rootId, rootDepth, rootVariables);

  return updated;
};

const getChildrenByParent = (tree) =>
  tree.reduce((acc, s) => {
    if (s.parentKey) {
      (acc[s.parentKey] ??= []).push(s);
    }
    return acc;
  }, {});

// A loop travels as a unit: the loop card, its body and the Next/Finish that
// closes it. Moving it with moveOrInsertStep instead would leave the body and
// the Next/Finish behind, reparented to wherever the loop was dragged from.
export const moveLoopStep = (
  state,
  { step, parentKey, label, id: childId, loopDepth = 0, loopVariables = [] }
) => {
  const loopNextStep = getLoopNextStep(state.steps, step);

  if (!loopNextStep) {
    // nothing closes this loop, so there is no unit to keep together
    moveOrInsertStep(state, {
      step,
      id: childId,
      parentKey,
      label,
      loopDepth,
      loopVariables,
    });
    return state.steps;
  }

  const stepsById = Object.fromEntries(state.steps.map((s) => [s.id, s]));
  const childrenByParent = getChildrenByParent(state.steps);

  // getStepIdsInLoop returns the body only, so seed both ends: the loop card
  // and its Next/Finish travel with it, and the walk below needs to re-depth
  // the loop card too.
  const movingGroup = new Set([
    step.id,
    ...getStepIdsInLoop(state.steps, step),
    loopNextStep.id,
  ]);

  if (parentKey === step.id || movingGroup.has(parentKey)) {
    // dropping a loop inside its own span would detach it from the tree
    return state.steps;
  }

  if (!childId && state.branches[parentKey]) {
    delete state.branches[parentKey];
  }

  const parentStep = stepsById[parentKey];
  const prevParentStep = stepsById[step.parentKey];

  const updatedLoopContext = getLoopContextForMovingGroup(
    stepsById,
    childrenByParent,
    movingGroup,
    step.id,
    loopDepth,
    loopVariables
  );

  // whatever ran after the loop stays put, taking the loop's old place
  const prevNextChildSteps = state.steps
    .filter((s) => s.parentKey === loopNextStep.id)
    .map(({ id }) => id);

  return state.steps.map((s) => {
    if (s.id === step.id) {
      // Reparent the loop card, but take its depth and variables from the
      // walk like every other step in the group: a loop opens a level, so it
      // sits one deeper than the position it was dropped on and carries its
      // own array variable at the front of the list.
      return {
        ...(updatedLoopContext[s.id] ?? s),
        parentKey: parentStep?.id || null,
        fromLabel: label?.type || '',
      };
    }

    // the step at the drop position now runs after the loop, so it hangs off
    // the Next/Finish rather than off the loop itself
    if (childId && s.id === childId) {
      return { ...s, parentKey: loopNextStep.id, fromLabel: '' };
    }

    if (prevNextChildSteps.includes(s.id)) {
      // could be null, so it sits next to variables
      return {
        ...s,
        parentKey: prevParentStep?.id || null,
        fromLabel: step?.fromLabel || '',
      };
    }

    return updatedLoopContext[s.id] ?? s;
  });
};

export const moveMergedPaths = (
  state,
  { step, parentKey, label, id: childId, loopDepth = 0, loopVariables = [] }
) => {
  const { mergeStep } = getMergeAndSplitSteps(state.steps, step);

  if (!childId && state.branches[parentKey]) {
    delete state.branches[parentKey];
  }

  const stepsById = Object.fromEntries(
    state.steps.map((step) => [step.id, step])
  );

  // merge steps are going between these nodes
  const parentStep = stepsById[parentKey];
  const prevParentStep = stepsById[step.parentKey];

  const stepsToPlaceAfterTheMerge = childId ? [childId] : [];

  const childrenByParent = getChildrenByParent(state.steps);

  // The steps of the group being moved, which stops at the merge step - the
  // steps after the merge stay where they are, so they keep the depth they had.
  const movingGroup = new Set(
    getStepsInMergeMovingBranch(step.id, state.steps)
  );

  // the moved step itself is set below, where it is reparented
  const stepToUpdateLoopAfterPlacing = getLoopContextForMovingGroup(
    stepsById,
    childrenByParent,
    movingGroup,
    step.id,
    loopDepth,
    loopVariables
  );

  // these get moved to where the Condition/Goal was
  const prevMergeChildSteps = state.steps
    .filter((s) => s.parentKey === mergeStep.id)
    .map(({ id }) => id);

  const nextSteps = state.steps.flatMap((s) => {
    // place moved step after parent
    if (s.id === step.id) {
      return {
        ...s,
        parentKey: parentStep?.id || null,
        fromLabel: label?.type || '',
        loopDepth,
        loopVariables,
      };
    }

    // place the new merge steps after the merge
    if (stepsToPlaceAfterTheMerge.includes(s.id)) {
      return { ...s, parentKey: mergeStep.id, fromLabel: '' };
    }

    // move steps that were after the merge to the moved step's previous parent
    if (prevMergeChildSteps.includes(s.id)) {
      // could be null, so it sits next to variables
      return {
        ...s,
        parentKey: prevParentStep?.id || null,
        fromLabel: step?.fromLabel || '',
      };
    }

    if (stepToUpdateLoopAfterPlacing[s.id]) {
      return stepToUpdateLoopAfterPlacing[s.id];
    }
    return s;
  });

  return nextSteps;
};

export const insertLoopNextStepAfterStep = (state, { id }) => {
  const childSteps = state.steps
    .filter((s) => s.parentKey === id)
    .map(({ id }) => id);
  let loopNextStep;

  const _loopSteps = state.steps.flatMap((step) => {
    if (isLoopStep(step) && step.id === id) {
      const nextStep = {
        ...step,
        loopVariables: [
          {
            loopStepId: step.id,
            variableName: step.details.forEachItem.arrayVariable.label,
          },
          ...step.loopVariables,
        ],
      };

      // A loop's depth counts the level it opens, but its Next/Finish sits
      // outside that level, at the depth of whatever encloses the loop - the
      // same as addLoopDepthToSteps gives it on load. Recording the loop's own
      // depth put a third-level loop's Next/Finish at the limit, which blocked
      // adding a loop after it until the automation was saved and reloaded.
      loopNextStep = createLoopNextStepData(
        step.id,
        Math.max((step.loopDepth ?? 0) - 1, 0),
        step.loopVariables,
        '',
        state.messageDictionary.nextFinishDescription
      );

      return [nextStep, loopNextStep];
    }
    return step;
  });

  const loopSteps = _loopSteps.map((step) => {
    if (childSteps.includes(step.id)) {
      return { ...step, parentKey: loopNextStep.id };
    }
    return step;
  });

  state.steps = loopSteps;

  return state;
};

// compares state.prevSteps & state.steps to figure out the new branch split
export const insertLoopNextStep = (state) => {
  const newStep = figureOutTheNewStep(state);
  if (!newStep) {
    return state;
  }
  return insertLoopNextStepAfterStep(state, newStep);
};

// A pasted loop must not reuse the item reference variable of the loop it was
// copied from - steps inside the new loop need their own, or the two loops
// would overwrite each other's current item. Generates a fresh name and
// initializes the variable alongside the others. Does nothing when the wizard
// already created one (i.e. the user picked a different array variable before
// saving), which keeps this safe to call on every paste.
export const addPastedLoopItemVariable = (state, pastedStep) => {
  const loop = state.steps.find(({ id }) => id === pastedStep.id);
  const arrayVariable = loop?.details?.forEachItem?.arrayVariable;

  if (!arrayVariable?.value) {
    return state;
  }

  const existingNames = getAllVariableNames(state.variables);
  const itemVariable = loop.details.forEachItem.itemVariable;

  // A name another loop still points at belongs to the loop this one was copied
  // from, so it has to be replaced. A name that exists but no other loop claims
  // was created for this paste by the wizard, so leave it be.
  const claimedByAnotherLoop = state.steps.some(
    (s) =>
      s.id !== loop.id &&
      isLoopStep(s) &&
      s.details?.forEachItem?.itemVariable === itemVariable
  );

  if (
    itemVariable &&
    existingNames.includes(itemVariable) &&
    !claimedByAnotherLoop
  ) {
    return state;
  }

  const sourceVariable = state.variables.find(
    (v) => v?.details?.variable?.name === arrayVariable.value
  );

  if (!sourceVariable) {
    return state;
  }

  const name = getUniqueItemVariableName(
    itemVariable || makeItemVariableName(arrayVariable.value),
    existingNames
  );

  state.variables = insertItemVariable(
    state.variables,
    createItemVariableStepData(
      name,
      arrayVariable.value,
      Date.now().toString(),
      'new.initialize_variable',
      state.variables.length.toString(),
      safeCurrent(sourceVariable.details)
    )
  );

  loop.details = {
    ...loop.details,
    forEachItem: { ...loop.details.forEachItem, itemVariable: name },
  };

  state.dirty = true;

  return state;
};

export const wrapStepWithBranch = ({
  steps,
  stepToWrap,
  parentKey,
  insertStepId,
  isBranchGroupInitiator,
  replace = false,
  additionBranchHead = undefined,
  inMergedBranch = false,
  messageDictionary,
}) => {
  const parentStep = steps.find((s) => s.id === parentKey);
  const parentIsBranchSplit = isBranchSplit(parentStep);

  // A step at the root of the flow has no parent, so the branch that wraps it
  // takes the root loop context rather than reading it off a missing step.
  const {
    loopDepth: parentLoopDepth = 0,
    loopVariables: parentLoopVariables = [],
  } = parentStep ?? {};

  const stepsToInsert = [];
  const duplicateStepId = `new.${stepToWrap.type}.${Date.now()}`;

  let duplicatedStepParentKey = undefined;

  if (parentIsBranchSplit) {
    if (!stepToWrap || inMergedBranch || !replace) {
      stepsToInsert.push({
        ...stepToWrap,
        id: duplicateStepId,
        isDuplicate: false,
        flashBackgroundColor: stepToWrap
          ? getStepConfig(
              stepToWrap.type === 'goal'
                ? stepToWrap?.details?.type
                : stepToWrap.type
            )?.bgColor
          : null,
        hasError: false,
        stepError: null,
        changeType: undefined,
      });
    }
    duplicatedStepParentKey = stepToWrap.parentKey;
  } else {
    const newBranchId = `new.${BranchStepTypes.BRANCH}.${Date.now()}`;
    // need to insert a branch and split

    const branch = createBranchStepData(
      newBranchId,
      parentKey,
      stepToWrap.fromLabel,
      isBranchGroupInitiator,
      isBranchGroupInitiator
        ? messageDictionary.runBranchesInParallelThenMerge
        : messageDictionary.runBranchesInParallel,
      stepToWrap.loopDepth,
      stepToWrap.loopVariables
    );

    const branchSplit = createBranchSplitStepData(
      newBranchId,
      isBranchGroupInitiator,
      false,
      null,
      parentLoopDepth,
      parentLoopVariables
    );

    stepsToInsert.push(branch, branchSplit);

    if (!replace) {
      stepsToInsert.push({
        ...stepToWrap,
        parentKey: branchSplit.id,
        id: duplicateStepId,
        isDuplicate: false,
        flashBackgroundColor: stepToWrap
          ? getStepConfig(
              stepToWrap.type === 'goal'
                ? stepToWrap?.details?.type
                : stepToWrap.type
            )?.bgColor
          : null,
        hasError: false,
        stepError: null,
        changeType: undefined,
        fromLabel: '',
        parentCondition: '',
      });
    }

    if (isBranchGroupInitiator) {
      const mergeStep = createMergeStepData(
        newBranchId,
        '',
        parentLoopDepth,
        parentLoopVariables
      );
      stepsToInsert.push(mergeStep);
    }
    duplicatedStepParentKey = branchSplit.id;
  }

  return {
    duplicateStepId,
    newSteps: steps.reduce((collect, el) => {
      if (el.id === insertStepId) {
        if (replace && parentIsBranchSplit && inMergedBranch) {
          return [...collect, ...stepsToInsert];
        } else {
          return [
            ...collect,
            ...stepsToInsert,
            {
              ...el,
              parentKey: duplicatedStepParentKey,
              fromLabel: '',
              parentCondition: '',
            },
          ];
        }
      }
      if (el.id === additionBranchHead?.id) {
        return [
          ...collect,
          {
            ...el,
            parentKey: duplicatedStepParentKey,
            fromLabel: '',
            parentCondition: '',
          },
        ];
      }

      return [...collect, el];
    }, []),
  };
};
